/**
 * Etapa 4 — Conclusão: o nível de risco do posto (com o que ele significa e a orientação), os possíveis danos
 * à saúde e as ações ergonômicas a adotar. Danos e ações gravam na hora; o nível e os dois textos seguem o
 * "Salvar e continuar" do Wizard.
 */
import React, { useCallback, useState } from 'react';
import { Alert, Box, Button, Chip, Grid, MenuItem, TextField, Typography } from '@mui/material';
import { ergonomicWorkstationService } from '../../../../../services/ergonomicWorkstationService';
import type { ErgonomicAction, ErgonomicDamage } from '../../../../../types/ergonomicWorkstation';
import { ActionDialog } from '../dialogs/ActionDialog';
import { DamageDialog } from '../dialogs/DamageDialog';
import { ConfirmDialog } from '../dialogs/ItemDialog';
import { ItemsBlock } from '../ItemsBlock';
import { useItems } from '../useItems';
import type { WizardStepProps, WorkstationDraft } from '../Wizard.types';
import { errorMessage } from '../wizardValidation';
import { WorkstationField } from '../WorkstationField';

const fieldSx = { '& .MuiFormHelperText-root': { fontSize: '1rem' } };

const formatDueDate = (iso: string | null): string => {
  if (!iso) return '';
  const [year, month, day] = iso.slice(0, 10).split('-');
  return `${day}/${month}/${year}`;
};

type DamageDialogState = { item: ErgonomicDamage | null } | null;
type ActionDialogState = { item: ErgonomicAction | null } | null;

export const ConclusionStep: React.FC<WizardStepProps> = ({
  workstation,
  options,
  values,
  errors,
  canEdit,
  disabled,
  onChange,
  onSaved,
}) => {
  const workstationId = workstation.id;
  const loadDamages = useCallback(() => ergonomicWorkstationService.getDamages(workstationId), [workstationId]);
  const loadActions = useCallback(() => ergonomicWorkstationService.getActions(workstationId), [workstationId]);
  const damages = useItems(loadDamages, 'Não foi possível carregar os danos à saúde.');
  const actions = useItems(loadActions, 'Não foi possível carregar as ações.');
  const [damageDialog, setDamageDialog] = useState<DamageDialogState>(null);
  const [actionDialog, setActionDialog] = useState<ActionDialogState>(null);
  const [removingDamage, setRemovingDamage] = useState<ErgonomicDamage | null>(null);
  const [removingAction, setRemovingAction] = useState<ErgonomicAction | null>(null);
  const [itemError, setItemError] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const common = { values, errors, onChange, canEdit, disabled };

  const confirmDamageRemoval = async () => {
    if (!removingDamage) return;
    try {
      await ergonomicWorkstationService.deleteDamage(workstationId, removingDamage.id);
      setRemovingDamage(null);
      await damages.reload();
      onSaved('Dano removido.');
    } catch (err: unknown) {
      setRemovingDamage(null);
      setItemError(errorMessage(err, 'Não foi possível remover o dano.'));
    }
  };

  const confirmActionRemoval = async () => {
    if (!removingAction) return;
    try {
      await ergonomicWorkstationService.deleteAction(workstationId, removingAction.id);
      setRemovingAction(null);
      await actions.reload();
      onSaved('Ação removida.');
    } catch (err: unknown) {
      setRemovingAction(null);
      setItemError(errorMessage(err, 'Não foi possível remover a ação.'));
    }
  };

  const toggleDone = async (action: ErgonomicAction) => {
    setTogglingId(action.id);
    setItemError(null);
    try {
      await ergonomicWorkstationService.updateAction(workstationId, action.id, { done: !action.done });
      await actions.reload();
      onSaved(action.done ? 'Ação marcada como não realizada.' : 'Ação marcada como realizada.');
    } catch (err: unknown) {
      setItemError(errorMessage(err, 'Não foi possível mudar a situação da ação.'));
    } finally {
      setTogglingId(null);
    }
  };

  const closeDamageSaved = async (message: string) => {
    setDamageDialog(null);
    await damages.reload();
    onSaved(message);
  };

  const closeActionSaved = async (message: string) => {
    setActionDialog(null);
    await actions.reload();
    onSaved(message);
  };

  return (
    <Box>
      <Typography variant="body1" sx={{ mb: 4 }}>
        Feche a avaliação do posto: diga o nível de risco, o que ele pode causar à saúde e o que será feito.
      </Typography>

      <Box component="section" sx={{ mb: 5 }}>
        <Typography variant="h6" component="h3" sx={{ mb: 2 }}>
          Nível de risco do posto
        </Typography>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              select
              fullWidth
              label="Nível de risco"
              value={values.riskLevel}
              onChange={(event) => onChange({ riskLevel: event.target.value as WorkstationDraft['riskLevel'] })}
              error={Boolean(errors.riskLevel)}
              helperText={errors.riskLevel || 'Escolha o nível que descreve o posto.'}
              disabled={disabled || !canEdit}
              sx={fieldSx}
            >
              <MenuItem value="">Ainda não definido</MenuItem>
              {(options?.riskLevels ?? []).map((level) => (
                <MenuItem key={level.value} value={level.value}>
                  {level.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <WorkstationField
            field="riskDescription"
            label="O que este nível significa"
            multiline
            md={8}
            help="Opcional. Explique o nível em uma ou duas frases."
            {...common}
          />
          <WorkstationField
            field="actionGuidance"
            label="Orientação"
            multiline
            help="Opcional. O que se recomenda para este nível de risco."
            {...common}
          />
        </Grid>
      </Box>

      {itemError && (
        <Alert severity="error" sx={{ fontSize: '1rem', mb: 2 }} onClose={() => setItemError(null)}>
          {itemError}
        </Alert>
      )}

      <ItemsBlock<ErgonomicDamage>
        title="Possíveis danos à saúde"
        description="O que o trabalho neste posto pode causar à pessoa que o ocupa."
        items={damages.items}
        loading={damages.loading}
        error={damages.error}
        onRetry={damages.reload}
        columns={[
          { id: 'title', label: 'Dano', primary: true, render: (row) => row.title },
          { id: 'description', label: 'Descrição', hideOnMobile: true, render: (row) => row.description ?? '—' },
        ]}
        emptyState={{
          title: 'Nenhum dano à saúde registrado neste posto',
          description: 'Use "Adicionar dano" para cada problema de saúde que este trabalho pode causar, como lombalgias.',
        }}
        addLabel="Adicionar dano"
        itemName="dano"
        itemLabel={(row) => row.title}
        canEdit={canEdit}
        onAdd={() => setDamageDialog({ item: null })}
        onEdit={(row) => setDamageDialog({ item: row })}
        onRemove={setRemovingDamage}
      />

      <ItemsBlock<ErgonomicAction>
        title="Ações ergonômicas a serem adotadas"
        description="O que será feito para reduzir o risco. Marque a situação de cada ação."
        items={actions.items}
        loading={actions.loading}
        error={actions.error}
        onRetry={actions.reload}
        columns={[
          { id: 'description', label: 'Ação', primary: true, render: (row) => row.description },
          {
            id: 'status',
            label: 'Situação',
            render: (row) => (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Chip
                  variant="outlined"
                  color={row.done ? 'success' : 'error'}
                  label={row.done ? '✔ Ação realizada' : '✘ Ação não realizada'}
                  sx={{ fontSize: '1rem', minHeight: 32 }}
                />
                {canEdit && (
                  <Button
                    variant="text"
                    disabled={disabled || togglingId === row.id}
                    onClick={() => toggleDone(row)}
                    sx={{ minHeight: 44 }}
                  >
                    {row.done ? 'Marcar como não realizada' : 'Marcar como realizada'}
                  </Button>
                )}
              </Box>
            ),
          },
          {
            id: 'dueDate',
            label: 'Prazo',
            hideOnMobile: true,
            render: (row) => formatDueDate(row.dueDate) || '—',
          },
          { id: 'responsible', label: 'Responsável', hideOnMobile: true, render: (row) => row.responsible ?? '—' },
        ]}
        emptyState={{
          title: 'Nenhuma ação registrada neste posto',
          description: 'Use "Adicionar ação" para cada medida que reduz o risco, como instalar uma bancada regulável.',
        }}
        addLabel="Adicionar ação"
        itemName="ação"
        itemLabel={(row) => row.description}
        canEdit={canEdit}
        onAdd={() => setActionDialog({ item: null })}
        onEdit={(row) => setActionDialog({ item: row })}
        onRemove={setRemovingAction}
      />

      {damageDialog && (
        <DamageDialog
          workstationId={workstationId}
          item={damageDialog.item}
          onSaved={closeDamageSaved}
          onClose={() => setDamageDialog(null)}
        />
      )}
      {actionDialog && (
        <ActionDialog
          workstationId={workstationId}
          item={actionDialog.item}
          onSaved={closeActionSaved}
          onClose={() => setActionDialog(null)}
        />
      )}
      {removingDamage && (
        <ConfirmDialog
          title="Remover este dano?"
          message="Ele some do cadastro do posto e do documento."
          onConfirm={confirmDamageRemoval}
          onCancel={() => setRemovingDamage(null)}
        />
      )}
      {removingAction && (
        <ConfirmDialog
          title="Remover esta ação?"
          message="Ela some do cadastro do posto e do documento."
          onConfirm={confirmActionRemoval}
          onCancel={() => setRemovingAction(null)}
        />
      )}
    </Box>
  );
};

export default ConclusionStep;
